import { useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";

const inputCls = "w-full h-11 px-4 bg-[#0F1115] border border-[#272B33] rounded-xl text-sm text-white focus:outline-none focus:border-blue-500";

export default function CategoriesPage() {
  const [tab, setTab] = useState("kategoriler");
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [catForm, setCatForm] = useState(null);
  const [brandForm, setBrandForm] = useState(null);

  const load = () => {
    api.get("/admin/categories").then(({ data }) => setCategories(data));
    api.get("/admin/brands").then(({ data }) => setBrands(data));
  };
  useEffect(load, []);

  const saveCategory = async (e) => {
    e.preventDefault();
    try {
      if (catForm.category_id) await api.put(`/admin/categories/${catForm.category_id}`, catForm);
      else await api.post("/admin/categories", catForm);
      toast.success("Kategori kaydedildi"); setCatForm(null); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const saveBrand = async (e) => {
    e.preventDefault();
    try {
      if (brandForm.brand_id) await api.put(`/admin/brands/${brandForm.brand_id}`, brandForm);
      else await api.post("/admin/brands", brandForm);
      toast.success("Marka kaydedildi"); setBrandForm(null); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const roots = categories.filter((c) => !c.parent_id);
  const flatten = (nodes, depth) => nodes.flatMap((c) => [
    { ...c, _depth: depth },
    ...flatten(categories.filter((x) => x.parent_id === c.category_id), depth + 1),
  ]);
  const tree = flatten(roots, 0);
  const parentOptions = tree.filter((c) => c.category_id !== catForm?.category_id && (c._depth ?? 0) < 2);

  return (
    <div className="space-y-5" data-testid="admin-categories">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display font-extrabold text-2xl text-white">Kategoriler & Markalar</h1>
        <div className="flex gap-2 bg-[#181B20] border border-[#272B33] rounded-xl p-1">
          {[["kategoriler", "Kategoriler"], ["markalar", "Markalar"]].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === id ? "bg-blue-600 text-white" : "text-neutral-400"}`}
              data-testid={`cat-tab-${id}`}>{label}</button>
          ))}
        </div>
      </div>

      {tab === "kategoriler" && (
        <>
          <div className="flex justify-end">
            <button onClick={() => setCatForm({ name: "", parent_id: "", image_url: "", banner_url: "", mobile_banner_url: "", description: "", seo_title: "", seo_description: "", seo_keywords: "", order: 0, active: true })}
              className="h-10 px-4 rounded-xl bg-blue-600 text-white text-sm font-semibold flex items-center gap-2 hover:bg-blue-500" data-testid="category-new">
              <Plus size={15} /> Yeni Kategori
            </button>
          </div>
          {catForm && (
            <form onSubmit={saveCategory} className="bg-[#181B20] border border-[#272B33] rounded-2xl p-5 space-y-3" data-testid="category-form">
              <div className="grid sm:grid-cols-4 gap-3 items-end">
                <input required placeholder="Kategori adı *" value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} className={inputCls} data-testid="category-name" />
                <select value={catForm.parent_id || ""} onChange={(e) => setCatForm({ ...catForm, parent_id: e.target.value || null })} className={inputCls} data-testid="category-parent">
                  <option value="">Ana kategori</option>
                  {parentOptions.map((c) => <option key={c.category_id} value={c.category_id}>{"— ".repeat(c._depth)}{c.name} altı</option>)}
                </select>
                <input placeholder="Görsel URL" value={catForm.image_url || ""} onChange={(e) => setCatForm({ ...catForm, image_url: e.target.value })} className={inputCls} data-testid="category-image" />
                <input placeholder="Açıklama" value={catForm.description || ""} onChange={(e) => setCatForm({ ...catForm, description: e.target.value })} className={inputCls} data-testid="category-description" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <input placeholder="Banner görseli URL" value={catForm.banner_url || ""} onChange={(e) => setCatForm({ ...catForm, banner_url: e.target.value })} className={inputCls} data-testid="category-banner" />
                <input placeholder="Mobil banner görseli URL" value={catForm.mobile_banner_url || ""} onChange={(e) => setCatForm({ ...catForm, mobile_banner_url: e.target.value })} className={inputCls} data-testid="category-mobile-banner" />
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <input placeholder="SEO başlık" value={catForm.seo_title || ""} onChange={(e) => setCatForm({ ...catForm, seo_title: e.target.value })} className={inputCls} data-testid="category-seo-title" />
                <input placeholder="SEO açıklama" value={catForm.seo_description || ""} onChange={(e) => setCatForm({ ...catForm, seo_description: e.target.value })} className={inputCls} data-testid="category-seo-description" />
                <input placeholder="SEO anahtar kelimeler" value={catForm.seo_keywords || ""} onChange={(e) => setCatForm({ ...catForm, seo_keywords: e.target.value })} className={inputCls} data-testid="category-seo-keywords" />
              </div>
              <div className="flex gap-2">
                <button type="submit" className="h-11 px-5 rounded-xl bg-blue-600 text-white text-sm font-semibold" data-testid="category-save">Kaydet</button>
                <button type="button" onClick={() => setCatForm(null)} className="h-11 px-4 rounded-xl border border-[#272B33] text-sm text-neutral-400">Vazgeç</button>
              </div>
            </form>
          )}
          <div className="bg-[#181B20] border border-[#272B33] rounded-2xl overflow-hidden">
            {tree.map((c) => (
              <div key={c.category_id} className="flex items-center justify-between px-5 py-3.5 border-b border-[#272B33]/50 last:border-0" data-testid={`category-row-${c.slug}`}>
                <div className="flex items-center gap-3">
                  {c.image_url && <img src={c.image_url} alt="" className="w-9 h-9 rounded-lg object-cover" />}
                  <div>
                    <p className="text-sm text-white font-medium" style={{ paddingLeft: `${(c._depth || 0) * 18}px` }}>{c._depth > 0 && <span className="text-neutral-500">{"↳ ".repeat(Math.min(c._depth, 2))}</span>}{c.name}
                      <span className="ml-2 text-[11px] text-neutral-500 font-normal" data-testid={`category-count-${c.slug}`}>({c.product_count ?? 0} ürün)</span>
                      {!c.active && <span className="ml-2 text-[10px] text-rose-400 font-mono-ed uppercase">pasif</span>}
                    </p>
                    <p className="text-[11px] text-neutral-500 font-mono-ed">/kategori/{c.slug}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setCatForm({ name: c.name, parent_id: c.parent_id || "", image_url: c.image_url || "", banner_url: c.banner_url || "", mobile_banner_url: c.mobile_banner_url || "", description: c.description || "", seo_title: c.seo_title || "", seo_description: c.seo_description || "", seo_keywords: c.seo_keywords || "", order: c.order, active: c.active, category_id: c.category_id })}
                    className="p-2 text-neutral-400 hover:text-blue-400" data-testid="category-edit"><Pencil size={15} /></button>
                  <button onClick={async () => { try { await api.delete(`/admin/categories/${c.category_id}`); load(); } catch (e) { toast.error(errMsg(e)); } }}
                    className="p-2 text-neutral-400 hover:text-rose-400" data-testid="category-delete"><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
            {categories.length === 0 && <p className="text-center text-neutral-500 text-sm py-10">Kategori yok</p>}
          </div>
        </>
      )}

      {tab === "markalar" && (
        <>
          <div className="flex justify-end">
            <button onClick={() => setBrandForm({ name: "", logo_url: "", active: true })}
              className="h-10 px-4 rounded-xl bg-blue-600 text-white text-sm font-semibold flex items-center gap-2 hover:bg-blue-500" data-testid="brand-new">
              <Plus size={15} /> Yeni Marka
            </button>
          </div>
          {brandForm && (
            <form onSubmit={saveBrand} className="bg-[#181B20] border border-[#272B33] rounded-2xl p-5 grid sm:grid-cols-4 gap-3 items-end" data-testid="brand-form">
              <input required placeholder="Marka adı *" value={brandForm.name} onChange={(e) => setBrandForm({ ...brandForm, name: e.target.value })} className={inputCls} data-testid="brand-name" />
              <input placeholder="Logo URL" value={brandForm.logo_url || ""} onChange={(e) => setBrandForm({ ...brandForm, logo_url: e.target.value })} className={inputCls} />
              <div />
              <div className="flex gap-2">
                <button type="submit" className="h-11 px-5 rounded-xl bg-blue-600 text-white text-sm font-semibold" data-testid="brand-save">Kaydet</button>
                <button type="button" onClick={() => setBrandForm(null)} className="h-11 px-4 rounded-xl border border-[#272B33] text-sm text-neutral-400">Vazgeç</button>
              </div>
            </form>
          )}
          <div className="bg-[#181B20] border border-[#272B33] rounded-2xl overflow-hidden">
            {brands.map((b) => (
              <div key={b.brand_id} className="flex items-center justify-between px-5 py-3.5 border-b border-[#272B33]/50 last:border-0" data-testid={`brand-row-${b.slug}`}>
                <div>
                  <p className="text-sm text-white font-medium">{b.name}</p>
                  <p className="text-[11px] text-neutral-500 font-mono-ed">/marka/{b.slug}</p>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setBrandForm({ name: b.name, logo_url: b.logo_url || "", active: b.active, brand_id: b.brand_id })}
                    className="p-2 text-neutral-400 hover:text-blue-400" data-testid="brand-edit"><Pencil size={15} /></button>
                  <button onClick={async () => { try { await api.delete(`/admin/brands/${b.brand_id}`); load(); } catch (e) { toast.error(errMsg(e)); } }}
                    className="p-2 text-neutral-400 hover:text-rose-400" data-testid="brand-delete"><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
            {brands.length === 0 && <p className="text-center text-neutral-500 text-sm py-10">Marka yok</p>}
          </div>
        </>
      )}
    </div>
  );
}
